import GuestInquiryActions from "./GuestInquiryActions";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

const emptyState = {
  inquiries: [],
  alerts: [],
  applications: [],
};

const WORKFLOW_STEPS = [
  "Pending",
  "Visited",
  "Documents Submitted",
  "Documents Approved",
  "Assigned",
];

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const pad = (value) => String(value).padStart(2, "0");

const toLocalDateInput = (value) => {
  const parsed = parseDate(value);
  if (!parsed) return "";
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};

const formatDateTime = (value) => {
  const parsed = parseDate(value);
  if (!parsed) return "No timestamp";
  return parsed.toLocaleString(undefined, {timeZone:'Asia/Manila'}) + ' (PH)';
};

const normalizeInquiry = (item) => ({
  id: item.id, source: "inquiry", fullName: item.name || "Guest", email: item.email,
  phone: item.phone || "No phone", propertyName: item.apartment_name || "General inquiry",
  location: "See apartment listing", maxBudget: "", preferredDate: item.preferred_date || "",
  preferredTime: item.preferred_time || "", message: item.message, createdAt: item.created_at,
  inquiryType: item.inquiry_type, inquiryStatus: item.status, confirmedDate: item.confirmed_date,
  confirmedTime: item.confirmed_time, adminNote: item.admin_note,
});

const normalizeApplication = (item, index = 0) => ({
  id: item.id || `application-${index}`,
  source: "application",
  fullName: item.full_name || item.fullName || item.applicant_name || "Applicant",
  email: item.email || "No email",
  phone: item.phone || "No phone",
  propertyName: item.apartment_name || item.property_name || "Apartment Unit",
  location: item.current_address || "No address provided",
  maxBudget: item.monthly_rent || item.rent_price || "",
  preferredDate: item.preferred_move_in_date || item.move_in_date || "",
  preferredTime: "",
  preferredViewingDate: item.preferred_viewing_date || "",
  message: item.message_to_landlord || "Rental application submitted.",
  createdAt: item.submitted_at || item.created_at || "",
  applicationStatus: item.application_status || item.status || "Pending",
  leaseDuration: item.lease_duration || "",
  viewingScheduledAt: item.viewing_scheduled_at || "",
  viewingNote: item.viewing_note || "",
  visitedAt: item.visited_at || "",
  tenantViewedAt: item.tenant_viewed_at || "",
  landlordViewedAt: item.landlord_viewed_at || "",
  documentsReviewStatus: item.documents_review_status || "pending",
  documentsSubmittedAt: item.documents_submitted_at || "",
  workflowNextStep: item.workflow_next_step || "",
  adminNote: item.admin_note || "",
  hasDocuments: Boolean(item.has_documents),
  unitId: item.unit_id || "",
  documentBarangay: item.document_barangay_clearance || "",
  documentValidId: item.document_valid_id || "",
  documentProofOfIncome: item.document_proof_of_income || "",
});

const applicationBadgeClass = (status) => {
  if (status === "Assigned" || status === "Approved") {
    return "border-emerald-300/30 bg-emerald-500/15 text-emerald-200";
  }
  if (status === "Rejected" || status === "Cancelled") {
    return "border-red-300/30 bg-red-500/15 text-red-200";
  }
  if (status === "Documents Submitted") {
    return "border-cyan-300/30 bg-cyan-500/15 text-cyan-200";
  }
  if (status === "Viewing Scheduled" || status === "Visited" || status === "Documents Required") {
    return "border-indigo-300/30 bg-indigo-500/15 text-indigo-200";
  }
  if (status === "Reserved") {
    return "border-sky-300/30 bg-sky-500/15 text-sky-200";
  }
  return "border-amber-300/30 bg-amber-500/15 text-amber-200";
};

function WorkflowProgress({ status }) {
  const closed = status === "Rejected" || status === "Cancelled";
  if (closed) {
    return (
      <p className="mt-3 rounded-lg border border-red-300/20 bg-red-500/10 px-3 py-2 text-xs text-red-100">
        Workflow Closed: {status}
      </p>
    );
  }

  const stepIndex = WORKFLOW_STEPS.includes(status)
    ? WORKFLOW_STEPS.indexOf(status)
    : status === "Approved"
    ? WORKFLOW_STEPS.indexOf("Assigned")
    : -1;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {WORKFLOW_STEPS.map((step, index) => {
        const isDone = stepIndex > index;
        const isActive = stepIndex === index;
        return (
          <div key={step} className="flex items-center gap-2">
            <span
              className={`rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${
                isActive
                  ? "border-teal-300/40 bg-teal-500/20 text-teal-100"
                  : isDone
                  ? "border-emerald-300/35 bg-emerald-500/15 text-emerald-100"
                  : "border-white/15 bg-white/5 text-slate-300"
              }`}
            >
              {step}
            </span>
            {index < WORKFLOW_STEPS.length - 1 ? (
              <span className="h-px w-3 bg-white/20" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ActionDialog({ dialog, busy, onClose, onChange, onSubmit }) {
  if (!dialog.open || !dialog.lead) return null;

  const title = dialog.type === "refuseDocs" ? "Refuse documents" : dialog.type === "reject" ? "Reject Application" : "Assign Unit";
  const submitLabel = dialog.type === "refuseDocs" ? "Refuse documents" : dialog.type === "reject" ? "Confirm Reject" : "Assign Unit";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-slate-950 p-5 shadow-2xl">
        <div className="mb-4 border-b border-white/10 pb-3">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-teal-300">Application Action</p>
          <h3 className="mt-2 text-xl font-bold text-white">{title}</h3>
          <p className="mt-1 text-xs text-slate-400">
            {dialog.lead.fullName} | {dialog.lead.propertyName}
          </p>
        </div>

        {dialog.type === "refuseDocs" || dialog.type === "reject" ? (
          <label className="block text-sm font-semibold text-slate-200">
            Note to Tenant
            <textarea
              rows={4}
              value={dialog.values.note || ""}
              onChange={(event) => onChange("note", event.target.value)}
              className="mt-2 w-full rounded-lg border border-white/10 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            />
          </label>
        ) : null}

        {dialog.type === "assign" ? (
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-slate-200">
              Lease Start Date (Optional)
              <input
                type="date"
                value={dialog.values.leaseStartDate || ""}
                onChange={(event) => onChange("leaseStartDate", event.target.value)}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
            </label>
            <label className="block text-sm font-semibold text-slate-200">
              Assignment Note (Optional)
              <textarea
                rows={3}
                value={dialog.values.assignmentNote || ""}
                onChange={(event) => onChange("assignmentNote", event.target.value)}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
            </label>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy}
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:opacity-60"
          >
            {busy ? "Saving..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentPreviewModal({ document, onClose }) {
  if (!document) return null;

  const mimeMatch = /^data:([^;]+);/i.exec(String(document.src || ""));
  const mimeType = (mimeMatch?.[1] || "").toLowerCase();
  const isPdf = mimeType.includes("pdf");
  const isImage = mimeType.startsWith("image/");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col rounded-xl border border-white/10 bg-slate-950 p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-teal-300">Document Preview</p>
            <h3 className="mt-1 text-lg font-bold text-white">{document.label}</h3>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={document.src}
              download={document.fileName || `${document.label.replace(/\s+/g, "_").toLowerCase()}`}
              className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-100 transition hover:bg-white/10"
            >
              Download
            </a>
            <button
              onClick={onClose}
              className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-100 transition hover:bg-white/10"
            >
              Close
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-white/10 bg-slate-900/70">
          {isPdf ? (
            <iframe
              title={document.label}
              src={document.src}
              className="h-[78vh] w-full rounded"
            />
          ) : isImage ? (
            <img
              src={document.src}
              alt={document.label}
              className="h-[78vh] w-full rounded object-contain"
            />
          ) : (
            <div className="flex h-[78vh] items-center justify-center px-4 text-center text-sm text-slate-300">
              Preview is not available for this file type. Use Download.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminLeadsInbox() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState({ ...emptyState });
  const [loadSummary, setLoadSummary] = useState("");
  const [actionBusyId, setActionBusyId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "",
    lead: null,
    values: {},
  });

  const loadLeads = useCallback(async ({ isManual = false, successMessage = '' } = {}) => {
    if (successMessage) { setActionMessage(successMessage); setActionError(''); }
    if (isManual) setRefreshing(true);
    else setLoading(true);

    try {
      const [inquiryResult, rentalResult] = await Promise.allSettled([
        api.get("/admin/inquiries"), api.get("/admin/rental-applications"),
      ]);
      setLeads({
        inquiries: inquiryResult.status === "fulfilled" ? toArray(inquiryResult.value.data).map(normalizeInquiry) : [],
        applications: rentalResult.status === "fulfilled" ? toArray(rentalResult.value.data).map(normalizeApplication) : [],
        alerts: [],
      });
      const missing = [inquiryResult.status === "rejected" ? "inquiries" : "", rentalResult.status === "rejected" ? "applications" : ""].filter(Boolean);
      setLoadSummary(missing.length ? "Unable to load " + missing.join(" and ") + ". Please try Refresh." : "Inquiries and viewing requests are received directly from visitors.");
    } catch (error) {
      setLoadSummary("Unable to load leads. Please try Refresh.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const allLeads = useMemo(
    () =>
      [...leads.inquiries, ...leads.alerts, ...leads.applications].sort((a, b) => {
        const aTime = parseDate(a.createdAt)?.getTime() || 0;
        const bTime = parseDate(b.createdAt)?.getTime() || 0;
        return bTime - aTime;
      }),
    [leads.alerts, leads.applications, leads.inquiries]
  );

  const visibleLeads = useMemo(() => {
    const pool =
      activeTab === "viewings"
        ? leads.inquiries.filter((lead) => lead.inquiryType === "viewing")
        : activeTab === "inquiries"
        ? leads.inquiries
        : activeTab === "alerts"
        ? leads.alerts
        : activeTab === "applications"
        ? leads.applications
        : allLeads;

    const query = search.trim().toLowerCase();
    if (!query) return pool;
    return pool.filter((lead) => {
      const text = `${lead.fullName} ${lead.email} ${lead.phone} ${lead.location} ${lead.propertyName} ${lead.message}`.toLowerCase();
      return text.includes(query);
    });
  }, [activeTab, allLeads, leads.alerts, leads.applications, leads.inquiries, search]);

  const runStatusUpdate = async (lead, applicationStatus, extra = {}) => {
    setActionBusyId(lead.id);
    setActionMessage("");
    setActionError("");
    try {
      await api.put(`/admin/rental-applications/${lead.id}/status`, {
        application_status: applicationStatus,
        ...extra,
      });
      setActionMessage(`Application moved to "${applicationStatus}".`);
      await loadLeads({ isManual: true });
      setDialog({ open: false, type: "", lead: null, values: {} });
    } catch (error) {
      console.error(error);
      setActionError(error?.response?.data?.message || "Unable to update application status.");
    } finally {
      setActionBusyId(null);
    }
  };

  const runAssignUnit = async (lead, payload) => {
    setActionBusyId(lead.id);
    setActionMessage("");
    setActionError("");
    try {
      await api.put(`/admin/rental-applications/${lead.id}/assign-unit`, payload);
      setActionMessage("Unit assigned. Tenant now moves into active tenant flow.");
      await loadLeads({ isManual: true });
      setDialog({ open: false, type: "", lead: null, values: {} });
    } catch (error) {
      console.error(error);
      setActionError(error?.response?.data?.message || "Unable to assign unit.");
    } finally {
      setActionBusyId(null);
    }
  };

  const openDialog = (type, lead) => {
    const defaults = ["refuseDocs", "reject"].includes(type)
      ? { note: "" }
      : { leaseStartDate: toLocalDateInput(lead.preferredDate) || "", assignmentNote: "" };

    setDialog({
      open: true,
      type,
      lead,
      values: defaults,
    });
  };

  const closeDialog = () => {
    if (actionBusyId) return;
    setDialog({ open: false, type: "", lead: null, values: {} });
  };

  const changeDialogValue = (field, value) => {
    setDialog((prev) => ({
      ...prev,
      values: {
        ...(prev.values || {}),
        [field]: value,
      },
    }));
  };

  const submitDialog = async () => {
    if (!dialog.lead) return;

    if (dialog.type === "refuseDocs") {
      if (!String(dialog.values.note || "").trim()) { setActionError("Explain what the tenant needs to correct."); return; }
      await runStatusUpdate(dialog.lead, "Documents Required", {
        admin_note: String(dialog.values.note || "").trim() || null,
      });
      return;
    }

    if (dialog.type === "reject") {
      await runStatusUpdate(dialog.lead, "Rejected", {
        admin_note: String(dialog.values.note || "").trim() || null,
      });
      return;
    }

    await runAssignUnit(dialog.lead, {
      lease_start_date: String(dialog.values.leaseStartDate || "").trim() || null,
      assignment_note: String(dialog.values.assignmentNote || "").trim() || null,
    });
  };

  const openDocument = (label, src) => {
    if (!src) return;
    setSelectedDocument({ label, src });
  };

  const markViewed = async (lead) => {
    setActionBusyId(lead.id); setActionError(""); setActionMessage("");
    try {
      await api.put(`/admin/rental-applications/${lead.id}/viewed`);
      await loadLeads({ isManual: true });
      setActionMessage("Your viewing confirmation is saved.");
    } catch (error) { setActionError(error?.response?.data?.message || "Could not confirm viewing."); }
    finally { setActionBusyId(null); }
  };

  const renderApplicationActions = (lead) => {
    const busy = actionBusyId === lead.id;
    const closed = ["Assigned", "Approved", "Rejected", "Cancelled"].includes(lead.applicationStatus);
    const buttonClass = "rounded-lg border border-white/20 bg-teal-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50";
    return <div className="mt-3 flex flex-wrap gap-2">
      {!closed && !lead.landlordViewedAt && <button disabled={busy} className={buttonClass} onClick={() => markViewed(lead)}>Mark as viewed</button>}
      {!closed && lead.applicationStatus === "Documents Submitted" && lead.tenantViewedAt && lead.landlordViewedAt && <>
        <button disabled={busy} className={buttonClass} onClick={() => runStatusUpdate(lead, "Documents Approved")}>Approve documents</button>
        <button disabled={busy} className={buttonClass} onClick={() => openDialog("refuseDocs", lead)}>Refuse documents</button>
      </>}
      {lead.applicationStatus === "Documents Approved" && lead.documentsReviewStatus === "approved" && <button disabled={busy} className={buttonClass} onClick={() => openDialog("assign", lead)}>Assign Unit</button>}
      {!closed && <button disabled={busy} className="ws-text-link" onClick={() => openDialog("reject", lead)}>Reject application</button>}
    </div>;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <video data-workspace-background="true" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" src={bgVideo} autoPlay muted loop />
      <div className="absolute inset-0 bg-slate-950/92" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80" />

      <ActionDialog
        dialog={dialog}
        busy={Boolean(actionBusyId)}
        onClose={closeDialog}
        onChange={changeDialogValue}
        onSubmit={submitDialog}
      />
      <DocumentPreviewModal
        document={selectedDocument}
        onClose={() => setSelectedDocument(null)}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 rounded-xl border border-white/10 bg-slate-950/92 p-6 shadow-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-teal-300">Admin Inbox</p>
          <h1 className="mt-2 text-3xl font-bold text-white">Leads Inbox</h1>
          <p className="mt-2 text-sm text-slate-300">
            Manage guest inquiries, viewing requests, and tenant rental applications in one place.
          </p>
          {loadSummary ? <p className="mt-2 text-xs text-slate-400">{loadSummary}</p> : null}
        </header>

        {actionMessage ? (
          <div className="mb-4 rounded-xl border border-emerald-300/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            {actionMessage}
          </div>
        ) : null}
        {actionError ? (
          <div className="mb-4 rounded-xl border border-red-300/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {actionError}
          </div>
        ) : null}

        <section className="mb-4 grid gap-3 rounded-xl border border-white/10 bg-slate-950/92 p-4 shadow-2xl md:grid-cols-[auto_auto_auto_auto_minmax(0,1fr)_150px] md:items-center">
          {[
            ["all", "All"],
            ["inquiries", "Inquiries"],
            ["viewings", "Viewing Requests"],
            ["applications", "Applications"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                activeTab === key
                  ? "bg-teal-600 text-white"
                  : "border border-white/15 text-slate-200 hover:bg-white/10"
              }`}
            >
              {label}
            </button>
          ))}
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search lead name, email, phone, location..."
            className="rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
          <button
            onClick={() => loadLeads({ isManual: true })}
            disabled={refreshing}
            className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </section>

        {loading ? (
          <div className="rounded-xl border border-white/10 bg-slate-950/92 py-10 text-center text-slate-300 shadow-2xl">
            Loading leads...
          </div>
        ) : visibleLeads.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/20 bg-slate-950/92 py-10 text-center text-slate-300 shadow-2xl">
            No leads found for this view.
          </div>
        ) : (
          <div className="grid gap-3">
            {visibleLeads.map((lead) => (
              <article
                key={`${lead.source}-${lead.id}`}
                className="rounded-xl border border-white/10 bg-slate-950/94 p-4 shadow-2xl"
              >
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2 border-b border-white/10 pb-3">
                  <div>
                    <p className="text-sm font-bold text-white">{lead.fullName}</p>
                    <p className="mt-1 text-xs text-slate-300">{lead.email} | {lead.phone}</p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] ${
                        lead.source === "inquiry"
                          ? "border-emerald-300/30 bg-emerald-500/15 text-emerald-200"
                          : lead.source === "application"
                          ? applicationBadgeClass(lead.applicationStatus)
                          : "border-cyan-300/30 bg-cyan-500/15 text-cyan-200"
                      }`}
                    >
                      {lead.source === "inquiry"
                        ? (lead.inquiryType === "viewing" ? `Viewing (${lead.inquiryStatus})` : `Inquiry (${lead.inquiryStatus})`)
                        : lead.source === "application"
                        ? `Application (${lead.applicationStatus || "Pending"})`
                        : "Vacancy Alert"}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">{formatDateTime(lead.createdAt)}</p>
                  </div>
                </div>

                <div className="grid gap-2 text-sm text-slate-200 md:grid-cols-2">
                  <p><span className="text-slate-400">Context:</span> {lead.propertyName}</p>
                  <p><span className="text-slate-400">Location:</span> {lead.location}</p>
                  <p><span className="text-slate-400">Budget:</span> {lead.maxBudget ? `PHP ${Number(lead.maxBudget).toLocaleString()}` : "N/A"}</p>
                  <p>
                    <span className="text-slate-400">Preferred Viewing:</span>{" "}
                    {lead.source === "application" ? (lead.preferredViewingDate || "Not requested") : lead.preferredDate || lead.preferredTime
                      ? `${lead.preferredDate || "No date"} ${lead.preferredTime || ""}`.trim()
                      : "N/A"}
                  </p>
                </div>

                {lead.source === "application" ? (
                  <>
                    <div className="mt-3 grid gap-2 rounded-lg border border-white/10 bg-slate-900/70 px-3 py-3 text-sm text-slate-200 md:grid-cols-2">
                      <p>Lease Duration: <span className="text-slate-300">{lead.leaseDuration || "Not specified"}</span></p>
                      <p>Unit ID: <span className="text-slate-300">{lead.unitId || "Not set"}</span></p>
                      <p>Landlord viewed: <span className="text-slate-300">{lead.landlordViewedAt ? "Confirmed" : "Waiting"}</span></p>
                      <p>Tenant viewed: <span className="text-slate-300">{lead.tenantViewedAt ? "Confirmed" : "Waiting"}</span></p>
                      <p>Documents Submitted: <span className="text-slate-300">{formatDateTime(lead.documentsSubmittedAt)}</span></p>
                      <p>Documents Flag: <span className="text-slate-300">{lead.hasDocuments ? "Submitted" : "Pending"}</span></p>
                      {lead.workflowNextStep ? (
                        <p className="md:col-span-2">Next Step: <span className="text-slate-300">{lead.workflowNextStep}</span></p>
                      ) : null}
                      {lead.viewingNote ? (
                        <p className="md:col-span-2">Viewing Note: <span className="text-slate-300">{lead.viewingNote}</span></p>
                      ) : null}
                      {lead.adminNote ? (
                        <p className="md:col-span-2">Admin Note: <span className="text-slate-300">{lead.adminNote}</span></p>
                      ) : null}
                    </div>

                    <WorkflowProgress status={lead.applicationStatus} />
                  </>
                ) : null}

                <p className="mt-3 rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-sm leading-6 text-slate-300">
                  {lead.message || "No message provided."}
                </p>

                {lead.source === "application" && lead.hasDocuments ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {lead.documentBarangay ? (
                      <button
                        type="button"
                        onClick={() => openDocument("Barangay Clearance", lead.documentBarangay)}
                        className="rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:bg-white/10"
                      >
                        View Barangay Clearance
                      </button>
                    ) : null}
                    {lead.documentValidId ? (
                      <button
                        type="button"
                        onClick={() => openDocument("Valid ID", lead.documentValidId)}
                        className="rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:bg-white/10"
                      >
                        View Valid ID
                      </button>
                    ) : null}
                    {lead.documentProofOfIncome ? (
                      <button
                        type="button"
                        onClick={() => openDocument("Proof of Income", lead.documentProofOfIncome)}
                        className="rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:bg-white/10"
                      >
                        View Proof of Income
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {lead.source === "application" ? renderApplicationActions(lead) : null}
                {lead.source === "inquiry" && <GuestInquiryActions key={`${lead.id}-${lead.inquiryStatus}-${lead.confirmedDate}-${lead.confirmedTime}`} lead={lead} onSaved={loadLeads} />}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
